import {
  Dropdown,
  DropdownItem,
  DropdownListItem,
  SelectTrigger,
} from '@toggle/design-system';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import * as S from './ResampleSelector.styles';
import { ResampleIntervals } from './ResampleSelector.types';

export interface ResampleSelectorProps {
  selectedResample: ResampleIntervals;
  onChange: (selectedResample: ResampleIntervals) => void;
}

interface ResampleOption extends DropdownItem {
  type: ResampleIntervals;
}

export const ResampleSelector = ({
  selectedResample,
  onChange,
}: ResampleSelectorProps) => {
  const { t } = useTranslation('turbo-chart');
  const items: ResampleOption[] = Object.values(ResampleIntervals).map(
    resampleInterval => ({
      type: resampleInterval,
      label: t('chart:intervals.resample', {
        context: resampleInterval,
      }),
    })
  );

  const selectedResampleIndex = items.findIndex(
    item => item.type === selectedResample
  );
  const [activeItemIdx, setActiveItemIdx] = useState(selectedResampleIndex);
  useEffect(() => {
    setActiveItemIdx(selectedResampleIndex);
  }, [selectedResampleIndex]);

  const onSelectItem = (
    item: ResampleOption,
    trigger: SelectTrigger,
    index: number
  ) => {
    onChange(item.type);
    setActiveItemIdx(index);
  };

  return (
    <S.ResampleSelectorRoot data-testid="interval-selector">
      <Dropdown
        dropdownItems={items}
        activeItemIdx={activeItemIdx}
        shouldShowItems={false}
        strategy="fixed"
        selectItem={onSelectItem}
        placement="bottom-start"
        renderDropdownItem={(item, isActive, isFocused) => (
          <DropdownListItem
            label={item.label}
            supportiveText={item.supportiveText}
            isActive={isActive}
            isFocused={isFocused}
          />
        )}
      >
        <S.StyledChip
          type="choice"
          label={t('chart:intervals.resample', {
            context: selectedResample,
          })}
          rightIcon="ChevronLightDown"
        />
      </Dropdown>
    </S.ResampleSelectorRoot>
  );
};
